import { defineStore } from 'pinia';
import { useProjectStore } from './projectStore';
import { ElMessageBox } from 'element-plus';
import notificationWrapper from '@/utils/helpers/notificationWrapper';
import { i18n } from '@/plugins/i18n';
import type { DemandState } from '@/types/Demand';
import { updateMinarmOrganismMessages } from '@/utils/helpers/minarmOrganism';

export const useDemandStore = defineStore('demand', {
  state: (): DemandState => ({
    demand: null,
    demandLoading: false,
    contractsTransfer: null,
    contractsTransferLoading: false,
    declarationLoading: false,
  }),

  actions: {
    async getDemand() {
      this.demandLoading = true;
      try {
        const projectStore = useProjectStore();
        const data = await window.appConnector.getDemand(projectStore.projectIdSelected);
        this.setDemand(data);
        this.demandLoading = false;
        return data;
      } catch (error) {
        notificationWrapper('error', error.isAuthenticationError
          ? 'NOT_LOGGED'
          : 'DEFAULT');
        this.demandLoading = false;
        throw error;
      }
    },

    async postValorDeclaration(formData: any) {
      this.declarationLoading = true;
      try {
        const projectStore = useProjectStore();
        const data = await window.appConnector.postValorDeclaration(projectStore.projectIdSelected, formData);
        if (this.demand) {
          this.demand.dv_docs = data;
        }
        notificationWrapper('success', 'POST_DECLARATION');
        this.declarationLoading = false;
        return data;
      } catch (error) {
        if (error?.data && ['ALLOW_PDF_ONLY',
          'FILE_INVALID', 'POST_DECLARATION_VALOR_FILES_TOO_BIG']
          .findIndex((msg) => error.data.error === msg) !== -1) {
          notificationWrapper('error', error.data.error);
          this.declarationLoading = false;
          return;
        }
        notificationWrapper();
        this.declarationLoading = false;
        throw error;
      }
    },

    async postCardToken(form: any) {
      this.demandLoading = true;
      try {
        const projectStore = useProjectStore();
        const data = await window.appConnector.postCardToken(projectStore.projectIdSelected, form);
        this.setDemand(data);
        notificationWrapper('success', 'POST_CB_SAVE');
        this.demandLoading = false;
        return data;
      } catch (error) {
        notificationWrapper();
        this.demandLoading = false;
        throw error;
      }
    },

    async getContractsTransfer() {
      this.contractsTransferLoading = true;
      try {
        const projectStore = useProjectStore();
        const data = await window.appConnector.getContractsTransfer(projectStore.projectIdSelected);
        this.contractsTransfer = data.requested_transfer;
        this.contractsTransferLoading = false;
        return data;
      } catch (error) {
        notificationWrapper('error', error.isAuthenticationError
          ? 'NOT_LOGGED'
          : 'DEFAULT');
        this.contractsTransferLoading = false;
        throw error;
      }
    },

    async postContractsTransfer(form: any) {
      this.contractsTransferLoading = true;
      try {
        const projectStore = useProjectStore();
        const data = await window.appConnector.postContractsTransfer(projectStore.projectIdSelected, form);
        this.contractsTransfer = data.requested_transfer;
        await this.getDemand();
        if (data.requested_transfer === 1 && form.showAlert !== false) {
          await ElMessageBox.alert(
            i18n.global.t('store.success.POST_CONTACT_TRANSFERT_TRUE.text'),
            i18n.global.t('store.success.POST_CONTACT_TRANSFERT_TRUE.title'),
            {
              confirmButtonText: i18n.global.t('store.success.POST_CONTACT_TRANSFERT_TRUE.btn'),
            },
          );
        }
        this.contractsTransferLoading = false;
        return data;
      } catch (error) {
        notificationWrapper('error', error.isAuthenticationError
          ? 'NOT_LOGGED'
          : 'DEFAULT');
        this.contractsTransferLoading = false;
        throw error;
      }
    },

    setDemand(demand: any) {
      this.demand = demand ? {
        ...demand,
        priceTypeKey: demand.show_price_ht ? 1 : 2,
      } : null;
      updateMinarmOrganismMessages(!!demand?.minarm_data?.is_minarm_gn);
    },
  },
});
